/**
 * The one time code panel, and the few helpers every sign in shares.
 *
 * One implementation for both apps: the member's sign in and sign up, and the
 * staff second factor in the operator console. Six boxes, a 120 second
 * expiry the panel counts down, and a resend that unlocks at 60 seconds. The
 * expiry is a validity, not a resend interval: the two run side by side and
 * they are not the same number.
 *
 * Nothing here starts an interval. The market clock already ticks once for
 * the whole document, so the panel reads its `now` and subtracts. It does not
 * use `clock.remaining`, because a code expires at three in the morning just
 * as it does at noon: the freeze rule belongs to offer validity, not to this.
 *
 * Every throttle the server applies arrives as a number of seconds, so the
 * panel draws a running clock rather than a sentence about waiting.
 */

import { useEffect, useRef, useState } from 'react'
import { formatCountdown } from '@metal/shared'
import { Button, ErrorText, Otp } from './kit'
import { ApiError } from './api'
import { useMarketClock } from './session'

/** `POST /auth/otp/request`, `/auth/otp/resend` and the staff second factor. */
export interface OtpChallenge {
  challengeId: string
  /** 120 by contract. The panel counts this down, it never resends on it. */
  expiresInSeconds: number
  /** 60 by contract. The resend link unlocks when it reaches zero. */
  resendAfterSeconds: number
  /** Pre formatted «۰۹۱۲ ۳۴۵ ۶۷۸۹». */
  maskedMobile: string
}

/**
 * «۰۱:۴۸». The shared countdown helper is hours first, which a two minute
 * code does not need; the hour group is dropped rather than re-formatted.
 */
export function mmss(seconds: number): string {
  return formatCountdown(seconds).replace(/^۰۰:/, '')
}

/** A mobile the server would refuse. Checked here so no SMS is ever spent. */
export function isMobileShaped(value: string): boolean {
  return /^09\d{9}$/.test(value)
}

const LOCK_CODES = ['MOBILE_LOCKED', 'OTP_DAILY_CAP', 'OTP_IP_CAP']

export interface OtpPanelProps {
  challenge: OtpChallenge
  /**
   * Throws an `ApiError`; the panel turns the code into a state.
   *
   * The id comes back with the code because a resend mints a new challenge
   * and the old one stops existing. The panel is the only part that knows
   * which challenge the boxes on screen belong to.
   */
  onVerify: (code: string, challengeId: string) => Promise<void>
  /** Mints a fresh challenge for the same number. */
  onResend: () => Promise<OtpChallenge>
  /** Back to the number. */
  onEdit: () => void
  submitLabel: string
  /** The heading over the code. `null` inside a modal, whose title already names it. */
  title?: string | null
}

export function OtpPanel({
  challenge,
  onVerify,
  onResend,
  onEdit,
  submitLabel,
  title = 'تأیید شماره موبایل',
}: OtpPanelProps) {
  const clock = useMarketClock()
  const nowMs = clock.now.getTime()
  const nowRef = useRef(nowMs)
  nowRef.current = nowMs

  const [live, setLive] = useState(challenge)
  const [code, setCode] = useState('')
  const [failure, setFailure] = useState<ApiError | null>(null)
  const [busy, setBusy] = useState(false)
  const [deadline, setDeadline] = useState(() => nowMs + challenge.expiresInSeconds * 1000)
  const [resendAt, setResendAt] = useState(() => nowMs + challenge.resendAfterSeconds * 1000)
  const [lockUntil, setLockUntil] = useState<number | null>(null)

  useEffect(() => {
    setLive(challenge)
    setCode('')
    setFailure(null)
    setLockUntil(null)
    setDeadline(nowRef.current + challenge.expiresInSeconds * 1000)
    setResendAt(nowRef.current + challenge.resendAfterSeconds * 1000)
  }, [challenge])

  const remaining = Math.max(0, Math.ceil((deadline - nowMs) / 1000))
  const resendIn = Math.max(0, Math.ceil((resendAt - nowMs) / 1000))
  const lockIn = lockUntil === null ? 0 : Math.max(0, Math.ceil((lockUntil - nowMs) / 1000))

  const locked = lockIn > 0
  const expired =
    !locked &&
    (remaining === 0 || Boolean(failure?.is('OTP_EXPIRED')) || Boolean(failure?.is('OTP_ALREADY_USED')))
  const invalid = locked || expired || Boolean(failure?.is('OTP_INVALID'))
  /** One clock: the code's life. A new code is offered once this one runs out. */
  const waitIn = locked ? lockIn : expired ? resendIn : Math.max(remaining, resendIn)

  /** One place turns a refusal into a clock. */
  const absorb = (e: unknown) => {
    if (!(e instanceof ApiError)) return
    setFailure(e)
    const wait = e.retryAfterSeconds
    if (wait !== null && LOCK_CODES.includes(e.code)) setLockUntil(nowRef.current + wait * 1000)
    if (wait !== null && e.is('OTP_RESEND_TOO_SOON')) setResendAt(nowRef.current + wait * 1000)
  }

  const submit = async () => {
    if (code.length < 6 || busy || locked || expired) return
    setBusy(true)
    try {
      await onVerify(code, live.challengeId)
      setFailure(null)
    } catch (e) {
      absorb(e)
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  const resend = async () => {
    if (waitIn > 0 || busy || locked) return
    setBusy(true)
    try {
      const next = await onResend()
      setLive(next)
      setCode('')
      setFailure(null)
      setDeadline(nowRef.current + next.expiresInSeconds * 1000)
      setResendAt(nowRef.current + next.resendAfterSeconds * 1000)
    } catch (e) {
      absorb(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="col" style={{ gap: 9 }}>
        {title && <h1 className="t-h1">{title}</h1>}
        <div className="row wrap" style={{ gap: 12, alignItems: 'center' }}>
          <p className="t-body t-sec" style={{ margin: 0 }}>
            کد شش‌رقمی به <span className="num num-ltr t-sb">{live.maskedMobile}</span> پیامک شد.
          </p>
          <Button variant="quiet" size="sm" className="t-sm" onClick={onEdit}>
            ویرایش
          </Button>
        </div>
      </div>

      <div className="col" style={{ gap: 16 }}>
        {/* Enter in the boxes sends the code, as it sends the password before it. */}
        <div
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            void submit()
          }}
        >
          <Otp
            value={code}
            onChange={locked || expired ? () => undefined : setCode}
            invalid={invalid}
            autoFocus
          />
        </div>

        <div className="row-b">
          <span className="t-sm t-muted" role="status">
            {locked ? (
              <>
                <span className="num t-sb">{mmss(lockIn)}</span> دیگر دوباره تلاش کنید
              </>
            ) : waitIn > 0 ? (
              <>
                ارسال مجدد کد تا <span className="num t-sb">{mmss(waitIn)}</span> دیگر
              </>
            ) : (
              'اعتبار کد تمام شد'
            )}
          </span>
          {/* One clock: the line above. The resend shows once it runs out. */}
          {!locked && waitIn === 0 && (
            <Button variant="quiet" size="sm" className="t-sm" onClick={() => void resend()}>
              ارسال مجدد کد
            </Button>
          )}
        </div>

        {failure && !failure.is('OTP_RESEND_TOO_SOON') && <ErrorText>{failure.message}</ErrorText>}
      </div>

      <Button
        variant="primary"
        size="lg"
        block
        loading={busy}
        disabled={code.length < 6 || locked || expired}
        onClick={() => void submit()}
      >
        {submitLabel}
      </Button>
    </>
  )
}
